/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Import /ezlibrary/helpers/* individualy. Do not import from /ezlibrary/helpers/EzHelpers.js'
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    DATA SOURCE (important):
    This reader consumes the ezclocker-services configuration API:

        GET /ezclocker/configuration/env  ->  application/json

    implemented by com.ezclocker.services.rest.EzClockerConfigurationRESTService.getEnv().
    The service reads ezclocker.properties server-side off the classpath and returns it as a
    flat JSON object of property-name to value:

        {
           "ezclocker.api.public.endpoint": "https://dev.ezclocker.com",
           "ezclocker.environment": "DEV",
           "ezclocker.website.baseurl": "https://dev.ezclocker.com"
        }

    Going through the service is what makes the configuration readable from a browser at all.
    The ezclocker-services 'ezclocker.properties' file is deployed by tomcat's webapps-config
    PreResources mount:

        <PreResources
            readOnly="true"
            className="org.apache.catalina.webresources.DirResourceSet"
            base="/var/lib/tomcat9/webapps-config"
            internalPath="/"
            webAppMount="/WEB-INF/classes" />

    That mount makes ezclocker.properties visible to SERVER-SIDE classpath lookups only.
    The servlet specification forbids a container from serving any resource under /WEB-INF
    to a browser, so an XHR/axios GET of '/WEB-INF/classes/ezclocker.properties' always
    returns 404. Only the service can read the file; never point this reader at that path.

    The service returns '{}' when it cannot read the file, so an empty configuration is a
    valid response and callers fall back to their supplied default values.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    EXPOSURE NOTE (EW-2216, corrected):
    /ezclocker/configuration/env is intentionally anonymous/unauthenticated - this reader
    calls it before any user session exists. It previously returned EVERY key in the deployed
    ezclocker.properties file to that anonymous caller; it is now restricted server-side to
    EzClockerConfigurationRESTService.PUBLIC_PROPERTY_NAMES (the 3-key response shape shown in
    this file's own header example: ezclocker.api.public.endpoint, ezclocker.environment,
    ezclocker.website.baseurl). No other key - including anything sensitive the deployed,
    not-checked-in-here properties file might contain - is ever returned. See the SECURITY
    NOTE on EzClockerConfigurationRESTService for the full history.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/
import axios from 'axios';

import { EzBadParamException } from '/ezlibrary/exceptions/EzExceptions.js';
import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzJson } from '/ezlibrary/helpers/EzJson.js';
import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';

/**
 * @class
 * @extends {EzStaticClass}
 * @description
 * Reads the ezclocker-services ezclocker.properties configuration through the
 * /ezclocker/configuration/env API and exposes the property values it returns.
 *
 * The configuration map and the in-flight request are both cached, so concurrent callers
 * share a single http request and repeat reads never re-request the configuration.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzPropertyReader } from '/ezlibrary/configuration/EzPropertyReader.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static access only: EzPropertyReader.{property or method}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Example use:
 *     EzPropertyReader.ezReadEzClockerVersion()
 *         .then((ezClockerVersion) => EzConsole.info(`ezClocker version: ${ezClockerVersion}`));
 *
 *     EzPropertyReader.ezReadProperty('ezclocker.api.public.endpoint', 'http://localhost:8080/')
 *         .then((endpoint) => { ... });
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzPropertyReader extends EzStaticClass {
    /**
     * @static
     * @public @readonly @property
     * Gets the ezclocker-services property file name the configuration API reads:
     * 'ezclocker.properties'
     * @returns {string}
     */
    static get EZCLOCKER_PROPERTIES_FILE_NAME() {
        return 'ezclocker.properties';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the ezclocker-services configuration API endpoint that returns the
     * ezclocker.properties configuration as a JSON map: '/ezclocker/configuration/env'
     * Provided by: com.ezclocker.services.rest.EzClockerConfigurationRESTService.getEnv()
     * @returns {string}
     */
    static get EZCLOCKER_CONFIGURATION_ENV_ENDPOINT() {
        return '/ezclocker/configuration/env';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default url the ezclocker.properties configuration is requested from:
     * '/ezclocker/configuration/env'
     * @returns {string}
     */
    static get DEFAULT_EZCLOCKER_PROPERTIES_URL() {
        return EzPropertyReader.EZCLOCKER_CONFIGURATION_ENV_ENDPOINT;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of the ezClocker version property: 'ezclocker.version'
     * @returns {string}
     */
    static get EZCLOCKER_VERSION_PROPERTY_NAME() {
        return 'ezclocker.version';
    }

    /**
     * @static
     * @private @field
     * Stores the url the ezclocker.properties configuration is requested from.
     * @type {string}
     */
    static #ezPropertiesUrl = EzPropertyReader.DEFAULT_EZCLOCKER_PROPERTIES_URL;
    /**
     * @static
     * @public @property @getter
     * Gets the url the ezclocker.properties configuration is requested from.
     * @returns {string}
     */
    static get ezPropertiesUrl() {
        return EzPropertyReader.#ezPropertiesUrl;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the url the ezclocker.properties configuration is requested from. The url must
     * return a flat JSON object of property-name to value, as /ezclocker/configuration/env does.
     * Assigning a different url clears any previously cached configuration.
     * @param {string} ezPropertiesUrl
     */
    static set ezPropertiesUrl(ezPropertiesUrl) {
        const updatedUrl = EzString.stringWithTextOrDefault(
            ezPropertiesUrl,
            EzPropertyReader.DEFAULT_EZCLOCKER_PROPERTIES_URL);

        if (updatedUrl !== EzPropertyReader.#ezPropertiesUrl) {
            EzPropertyReader.#ezPropertiesUrl = updatedUrl;

            EzPropertyReader.ezClearCachedProperties();
        }
    }

    /**
     * @static
     * @private @field
     * Stores the ezclocker.properties name/value pairs returned by the configuration API.
     * Null until a load completes.
     * @type {object}
     */
    static #ezProperties = null;
    /**
     * @static
     * @public @readonly @property
     * Gets the ezclocker.properties name/value pairs returned by the configuration API, or
     * null if the configuration has not been loaded yet.
     * @returns {object}
     */
    static get ezProperties() {
        return EzPropertyReader.#ezProperties;
    }

    /**
     * @static
     * @private @field
     * Stores the in-flight configuration load promise so that concurrent callers share a
     * single http request.
     * @type {Promise}
     */
    static #ezLoadPropertiesPromise = null;

    /**
     * @static
     * @public @readonly @property
     * Returns true if the ezclocker.properties configuration has been loaded.
     * @returns {boolean}
     */
    static get ezPropertiesLoaded() {
        return EzObject.isValid(EzPropertyReader.#ezProperties);
    }

    /**
     * @static
     * @public @method
     * Clears the cached configuration values, forcing the next read to re-request the
     * configuration API.
     */
    static ezClearCachedProperties() {
        EzPropertyReader.#ezProperties = null;

        EzPropertyReader.#ezLoadPropertiesPromise = null;
    }

    /**
     * @static
     * @public @method
     * Loads the ezclocker.properties configuration from EzPropertyReader.ezPropertiesUrl.
     *
     * Never rejects. If the request fails or the response is not a JSON object, the failure
     * is logged and an empty configuration resolves so callers fall back to their provided
     * default values.
     * @param {boolean} forceReload
     * Pass true to bypass the cache and re-request the configuration.
     * Default: false
     * @returns {Promise.resolve}
     * Resolves the configuration name/value map.
     */
    static ezLoadProperties(forceReload = false) {
        if (EzBoolean.isTrue(forceReload)) {
            EzPropertyReader.ezClearCachedProperties();
        }

        if (EzPropertyReader.ezPropertiesLoaded) {
            return Promise.resolve(EzPropertyReader.#ezProperties);
        }

        if (EzObject.isValid(EzPropertyReader.#ezLoadPropertiesPromise)) {
            // A load is already in-flight: share it instead of issuing a second request.
            return EzPropertyReader.#ezLoadPropertiesPromise;
        }

        EzPropertyReader.#ezLoadPropertiesPromise = EzPromise.asyncAction(
            (finished) => axios.get(
                EzPropertyReader.ezPropertiesUrl,
                {
                    headers: {
                        Accept: 'application/json'
                    }
                })
                .then(
                    (configurationResponse) => {
                        EzPropertyReader.#ezProperties = EzPropertyReader.ezResolveConfigurationMap(
                            configurationResponse?.data);

                        return finished(EzPropertyReader.#ezProperties);
                    },
                    (eResponse) => {
                        EzConsole.error(
                            EzString.em`
                                Unable to load the ${EzPropertyReader.EZCLOCKER_PROPERTIES_FILE_NAME}
                                configuration from ${EzPropertyReader.ezPropertiesUrl}.
                                Error response: ${eResponse}`);

                        // Cache an empty map so repeat reads do not re-request a url that failed.
                        EzPropertyReader.#ezProperties = {};

                        return finished(EzPropertyReader.#ezProperties);
                    }));

        return EzPropertyReader.#ezLoadPropertiesPromise;
    }

    /**
     * @static
     * @public @method
     * Reads the value of the provided property name from the ezclocker.properties
     * configuration, loading it from the configuration API first if it has not been loaded yet.
     * @param {string} propertyName
     * Required valid, non-empty property name.
     * @param {string} defaultValue
     * Returned when the property is absent from the configuration or the configuration could
     * not be loaded.
     * Default: EzString.EMPTY
     * @returns {Promise.resolve}
     * Resolves the property value, or the provided defaultValue.
     */
    static ezReadProperty(propertyName, defaultValue = EzString.EMPTY) {
        if (!EzString.hasText(propertyName)) {
            throw new EzBadParamException(
                'propertyName',
                EzPropertyReader,
                EzPropertyReader.ezReadProperty);
        }

        return EzPropertyReader.ezLoadProperties()
            .then(() => EzPropertyReader.ezPropertyValueOrDefault(propertyName, defaultValue));
    }

    /**
     * @static
     * @public @method
     * Reads the 'ezclocker.version' property value from the ezclocker.properties configuration.
     *
     * NOTE: no 'ezclocker.version' key currently exists in configurations/{env}/ezclocker.properties,
     * so this resolves the provided defaultValue. The ezClocker version values presently live in
     * version.properties / version.json as 'ezclocker.build.version', 'ezclocker.website.version'
     * and 'ezclocker.services.version'. Either add 'ezclocker.version' to ezclocker.properties, or
     * call EzPropertyReader.ezReadProperty() with whichever key is intended.
     * @param {string} defaultValue
     * Returned when 'ezclocker.version' is absent or the configuration could not be loaded.
     * Default: EzString.EMPTY
     * @returns {Promise.resolve}
     * Resolves the ezclocker.version property value, or the provided defaultValue.
     */
    static ezReadEzClockerVersion(defaultValue = EzString.EMPTY) {
        return EzPropertyReader.ezReadProperty(
            EzPropertyReader.EZCLOCKER_VERSION_PROPERTY_NAME,
            defaultValue);
    }

    /**
     * @static
     * @public @method
     * Reads a property value from the already loaded configuration cache without issuing a
     * request. Returns the provided defaultValue when the configuration is not loaded yet
     * or the property is absent.
     * @param {string} propertyName
     * Required valid, non-empty property name.
     * @param {string} defaultValue
     * Default: EzString.EMPTY
     * @returns {string}
     */
    static ezPropertyValueOrDefault(propertyName, defaultValue = EzString.EMPTY) {
        if (!EzString.hasText(propertyName)) {
            throw new EzBadParamException(
                'propertyName',
                EzPropertyReader,
                EzPropertyReader.ezPropertyValueOrDefault);
        }

        return EzObject.isValid(EzPropertyReader.#ezProperties) &&
            EzObject.hasOwnProperty(EzPropertyReader.#ezProperties, propertyName)
            ? EzPropertyReader.#ezProperties[propertyName]
            : defaultValue;
    }

    /**
     * @static
     * @public @method
     * Resolves the configuration API response body into a property name/value map.
     *
     * Axios parses an application/json body for us, so responseData is normally already an
     * object. A string body is still parsed here for the case where the response arrives
     * without a json content type. Anything that is not a json object resolves to an empty
     * map rather than being handed back to callers as a string.
     * @param {object|string} responseData
     * @returns {object}
     * Returns the property name/value map. An empty object is returned when responseData is
     * not a json object.
     */
    static ezResolveConfigurationMap(responseData) {
        let ezConfiguration = responseData;

        if (EzString.isString(ezConfiguration)) {
            if (!EzString.hasText(ezConfiguration)) {
                EzConsole.error(
                    EzString.em`
                        The ${EzPropertyReader.EZCLOCKER_PROPERTIES_FILE_NAME} configuration response
                        from ${EzPropertyReader.ezPropertiesUrl} was empty.`);

                return {};
            }

            // EzJson.fromJson returns the original string when the value is not valid json.
            ezConfiguration = EzJson.fromJson(ezConfiguration);
        }

        if (!EzObject.isObject(ezConfiguration) || Array.isArray(ezConfiguration)) {
            EzConsole.error(
                EzString.em`
                    Expected a json object of property names and values from
                    ${EzPropertyReader.ezPropertiesUrl}.
                    Received: ${responseData}`);

            return {};
        }

        return ezConfiguration;
    }
}
